<script setup lang="ts">
/**
 * ADR 0038 — the SME-facing "Adjust assistant" flow.
 *
 * Audience: domain experts who are NOT technical and NOT AI-savvy.
 * Design rules baked in:
 *   - Zero jargon (no "system prompt", "workflow slug", "diff",
 *     "override" in the primary path). Was Danish-only until the
 *     product settled on one UI language; the copy stayed deliberately
 *     plain when it moved to English.
 *   - Review = before/after cards of ONLY the changed passages —
 *     never a unified +/- diff (that's developer UX). Full text
 *     behind a details-toggle for the curious.
 *   - Reassurance up front: nothing can break, everything is
 *     one-click reversible (and that's true — assistant_revisions).
 *
 * Used in two places: embedded in the admin assistant editor, and as
 * a modal from the chat header for assistant owners (the SME's real
 * entry point — right where they saw the answer they want changed).
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type AssistantRevisionRow,
  type AssistantWorkflowRow,
  type RefinePromptResult,
  listRevisions,
  listWorkflows,
  refinePrompt,
  resetWorkflow,
  rollbackRevision,
} from "../api/admin";
import { useToast } from "../composables/useToast";
import { useConfirm } from "../composables/useConfirm";

const props = defineProps<{
  slug: string;
  assistantName?: string;
  devUser: string;
}>();

const toast = useToast();
const { confirm } = useConfirm();

const instruction = ref("");
const target = ref(""); // "" = the assistant in general (system prompt)
const busy = ref(false);
const result = ref<RefinePromptResult | null>(null);
const applied = ref(false);
const workflows = ref<AssistantWorkflowRow[]>([]);
const revisions = ref<AssistantRevisionRow[]>([]);
const showFullText = ref(false);

function _toastError(e: unknown, context: string): void {
  toast.error(context, e instanceof AdminError ? e.message : String(e));
}

/** Friendly display name for a workflow: the title from its body's
 * first line ("WORKFLOW: Meeting Briefing (mødebriefing)") when
 * present, else the prettified slug. */
function workflowLabel(w: AssistantWorkflowRow): string {
  const m = /^WORKFLOW:\s*(.+)$/m.exec(w.body);
  if (m) return m[1].trim();
  return w.name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** A routing regex, rendered so a human can see what fires the workflow.
 *
 * These are real patterns from keywords.json, not prose. Showing them raw
 * would be noise; paraphrasing them would be a lie about what actually
 * matches. So strip the regex furniture and keep the words.
 *
 * The diacritic alternations need collapsing first — `sidegr(æ|ae)ns`
 * otherwise reads as "sidegræ / aens", which is worse than the raw
 * pattern. Any group whose alternatives are all very short is one of
 * those spelling variants, never a real choice of words.
 */
function triggerWords(pattern: string): string {
  return pattern
    .replace(/\(([^()|]{1,3})(?:\|[^()|]{1,3})+\)/g, "$1")
    .replace(/\\b/g, " ")
    .replace(/\.\{\d+,\d+\}/g, " … ")
    .replace(/\\w\*/g, "")
    .replace(/\\s(?:\+|\*)?/g, " ")
    .replace(/[()\[\]?^$*+]/g, "")
    .replace(/\|/g, " / ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

async function loadContext(): Promise<void> {
  try {
    workflows.value = await listWorkflows(props.slug, props.devUser);
  } catch {
    workflows.value = [];
  }
  try {
    revisions.value = await listRevisions(props.slug, props.devUser);
  } catch {
    revisions.value = [];
  }
}
onMounted(() => void loadContext());

// ── before/after hunks (the SME-friendly review) ───────────────────────────

interface Hunk {
  before: string;
  after: string;
}

/** LCS line diff → grouped change hunks. Adjacent changes separated by
 * up to 2 unchanged lines merge into one hunk so a rephrased paragraph
 * reads as one before/after pair, not five slivers. */
function computeHunks(a: string, b: string): Hunk[] {
  const A = a.split("\n");
  const B = b.split("\n");
  const n = A.length;
  const m = B.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        A[i] === B[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  type Op = { t: " " | "+" | "-"; s: string };
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) {
      ops.push({ t: " ", s: A[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ t: "-", s: A[i++] });
    } else {
      ops.push({ t: "+", s: B[j++] });
    }
  }
  while (i < n) ops.push({ t: "-", s: A[i++] });
  while (j < m) ops.push({ t: "+", s: B[j++] });

  const hunks: Hunk[] = [];
  let cur: { del: string[]; add: string[] } | null = null;
  let gap = 0;
  for (const op of ops) {
    if (op.t === " ") {
      if (cur) {
        gap++;
        if (gap > 2) {
          hunks.push({ before: cur.del.join("\n"), after: cur.add.join("\n") });
          cur = null;
        }
      }
      continue;
    }
    if (!cur) {
      cur = { del: [], add: [] };
    }
    gap = 0;
    if (op.t === "-") cur.del.push(op.s);
    else cur.add.push(op.s);
  }
  if (cur)
    hunks.push({ before: cur.del.join("\n"), after: cur.add.join("\n") });
  return hunks.filter((h) => h.before.trim() || h.after.trim());
}

const hunks = computed<Hunk[]>(() =>
  result.value
    ? computeHunks(result.value.current_prompt, result.value.proposed_prompt)
    : [],
);

// ── actions ────────────────────────────────────────────────────────────────

async function propose(): Promise<void> {
  if (!instruction.value.trim()) return;
  busy.value = true;
  result.value = null;
  applied.value = false;
  showFullText.value = false;
  try {
    result.value = await refinePrompt(
      props.slug,
      {
        instruction: instruction.value.trim(),
        apply: false,
        ...(target.value ? { workflow: target.value } : {}),
      },
      props.devUser,
    );
  } catch (e) {
    _toastError(e, "Could not produce a proposal — try again");
  } finally {
    busy.value = false;
  }
}

async function apply(): Promise<void> {
  if (!result.value) return;
  busy.value = true;
  try {
    await refinePrompt(
      props.slug,
      {
        instruction: instruction.value.trim() || "(approved change)",
        apply: true,
        proposedPrompt: result.value.proposed_prompt,
        ...(target.value ? { workflow: target.value } : {}),
      },
      props.devUser,
    );
    applied.value = true;
    result.value = null;
    instruction.value = "";
    emit("applied");
    await loadContext();
  } catch (e) {
    _toastError(e, "Could not save the change");
  } finally {
    busy.value = false;
  }
}

function discard(): void {
  result.value = null;
  showFullText.value = false;
}

async function undo(rev: AssistantRevisionRow): Promise<void> {
  const ok = await confirm({
    title: "Revert to this version?",
    message:
      "The current version is kept in the history too, so you can always " +
      "undo again.",
    confirmLabel: "Revert",
    cancelLabel: "Cancel",
  });
  if (!ok) return;
  try {
    await rollbackRevision(props.slug, rev.id, props.devUser);
    toast.success("Version restored", "the change is live immediately");
    emit("applied");
    await loadContext();
  } catch (e) {
    _toastError(e, "Could not restore that version");
  }
}

async function resetOverride(w: AssistantWorkflowRow): Promise<void> {
  const ok = await confirm({
    title: `Reset "${workflowLabel(w)}" to default?`,
    message: "Your changes stay in the history, so nothing is lost.",
    confirmLabel: "Reset",
    cancelLabel: "Cancel",
  });
  if (!ok) return;
  try {
    await resetWorkflow(props.slug, w.name, props.devUser);
    toast.success("Reset", "the default version is live again");
    emit("applied");
    await loadContext();
  } catch (e) {
    _toastError(e, "Could not reset");
  }
}

function revisionLabel(rev: AssistantRevisionRow): string {
  if (!rev.workflow_name) return "General behaviour";
  const w = workflows.value.find((x) => x.name === rev.workflow_name);
  return w ? workflowLabel(w) : rev.workflow_name;
}

const emit = defineEmits<{ (e: "applied"): void }>();
</script>

<template>
  <div class="sme-refine">
    <p class="sme-refine__intro">
      Describe in your own words what
      <strong>{{ assistantName || slug }}</strong> should do differently. The AI
      proposes a change, which you review and approve before it takes effect.
      <em
        >You can't break anything — every change can be undone with one
        click.</em
      >
    </p>

    <label class="sme-refine__field">
      <span class="sme-refine__label">Where does the change apply?</span>
      <select v-model="target" @change="discard()">
        <option value="">
          Everywhere — the whole assistant (pick this if unsure)
        </option>
        <option v-for="w in workflows" :key="w.name" :value="w.name">
          Only in: {{ workflowLabel(w) }}
        </option>
      </select>
    </label>

    <label class="sme-refine__field">
      <span class="sme-refine__label">What should change?</span>
      <textarea
        v-model="instruction"
        rows="3"
        placeholder="e.g. When I ask for a meeting briefing, always end with three concrete questions I can put to the customer."
      />
    </label>

    <div class="sme-refine__actions">
      <button
        class="btn btn--primary"
        :disabled="busy || !instruction.trim()"
        v-write
        @click="propose"
      >
        {{ busy && !result ? "Thinking…" : "See proposal" }}
      </button>
    </div>

    <div v-if="applied" class="sme-refine__done">
      ✓ The change is live. Ask a question in the chat and see whether the
      answer is what you wanted — if not, undo it under History below.
    </div>

    <div v-if="result" class="sme-refine__review">
      <h4>Proposed change</h4>
      <p v-if="result.rationale" class="sme-refine__rationale">
        {{ result.rationale }}
      </p>

      <div v-if="hunks.length === 0" class="sme-refine__nochange">
        The AI proposed no text change — try describing what you want more
        concretely.
      </div>

      <div v-for="(h, idx) in hunks" :key="idx" class="sme-refine__hunk">
        <div v-if="h.before" class="sme-refine__before">
          <span class="sme-refine__tag">Before</span>
          <pre>{{ h.before }}</pre>
        </div>
        <div v-if="h.after" class="sme-refine__after">
          <span class="sme-refine__tag">After</span>
          <pre>{{ h.after }}</pre>
        </div>
      </div>

      <details class="sme-refine__full" :open="showFullText">
        <summary>Show the full new text</summary>
        <pre class="sme-refine__fulltext">{{ result.proposed_prompt }}</pre>
      </details>

      <div class="sme-refine__actions">
        <button class="btn" :disabled="busy" @click="discard">Discard</button>
        <button v-write class="btn" :disabled="busy" @click="propose">Try again</button>
        <button
          class="btn btn--primary"
          :disabled="busy || hunks.length === 0"
          v-write
          @click="apply"
        >
          {{ busy ? "Saving…" : "✓ Approve the change" }}
        </button>
      </div>
    </div>

    <div
      v-if="workflows.some((w) => w.overridden)"
      class="sme-refine__overrides"
    >
      <span class="sme-refine__label">Your active customisations:</span>
      <span
        v-for="w in workflows.filter((x) => x.overridden)"
        :key="w.name"
        class="pill"
      >
        {{ workflowLabel(w) }}
        <button
          class="sme-refine__pill-reset"
          title="Reset to default"
          v-write
          @click="resetOverride(w)"
        >
          ×
        </button>
      </span>
    </div>

    <details v-if="workflows.length" class="sme-refine__history">
      <summary>
        How this assistant works — {{ workflows.length }} situations it
        recognises
      </summary>
      <p class="sme-refine__hint">
        Each of these is a set of instructions the assistant follows only
        when your message matches it. The rest of the time it works from
        its general behaviour.
      </p>
      <div v-for="w in workflows" :key="w.name" class="sme-refine__wf">
        <div class="sme-refine__wf-head">
          <strong>{{ workflowLabel(w) }}</strong>
          <span class="pill">{{ w.overridden ? "customised" : "default" }}</span>
        </div>
        <p v-if="w.triggers?.length" class="sme-refine__wf-when">
          Fires on:
          <code v-for="t in w.triggers" :key="t">{{ triggerWords(t) }}</code>
        </p>
        <details class="sme-refine__wf-body">
          <summary>Show the full instructions</summary>
          <pre>{{ w.body }}</pre>
        </details>
      </div>
    </details>

    <details v-if="revisions.length" class="sme-refine__history">
      <summary>History — undo earlier changes ({{ revisions.length }})</summary>
      <table class="sme-refine__history-table">
        <tbody>
          <tr v-for="rev in revisions" :key="rev.id">
            <td>{{ rev.created_at.slice(0, 16).replace("T", " ") }}</td>
            <td>{{ revisionLabel(rev) }}</td>
            <td class="sme-refine__note" :title="rev.change_note">
              {{ rev.change_note || "(no description)" }}
            </td>
            <td>
              <button v-write class="btn btn--ghost" @click="undo(rev)">
                Revert to this
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </details>
  </div>
</template>

<style scoped>
.sme-refine__wf { margin: 0.5rem 0 0.75rem; }
.sme-refine__wf-head { display: flex; align-items: center; gap: 0.5rem; }
.sme-refine__wf-when { margin: 0.2rem 0; font-size: 0.8rem; color: var(--muted, #666); }
.sme-refine__wf-when code {
  display: inline-block;
  margin: 0.1rem 0.25rem 0.1rem 0;
  padding: 0 0.3rem;
  background: var(--code-bg, #f4f4f5);
  border-radius: 3px;
  font-size: 0.75rem;
}
.sme-refine__wf-body pre {
  white-space: pre-wrap;
  font-size: 0.75rem;
  background: var(--code-bg, #f7f7f8);
  padding: 0.5rem;
  border-radius: 4px;
  max-height: 22rem;
  overflow: auto;
}
.sme-refine {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.sme-refine__intro {
  font-size: 0.95rem;
  margin: 0;
}
.sme-refine__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.sme-refine__label {
  font-weight: 600;
  font-size: 0.85rem;
}
.sme-refine__field textarea,
.sme-refine__field select {
  width: 100%;
  font: inherit;
  padding: 0.4rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
}
.sme-refine__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.sme-refine__done {
  background: rgba(90, 160, 90, 0.14);
  border: 1px solid rgba(90, 160, 90, 0.4);
  border-radius: var(--radius-sm);
  padding: 0.6rem 0.8rem;
  font-size: 0.9rem;
}
.sme-refine__review h4 {
  margin: 0.25rem 0;
}
.sme-refine__rationale {
  background: rgba(39, 157, 182, 0.08);
  border-left: 3px solid #279db6;
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}
.sme-refine__nochange {
  color: var(--color-muted);
  font-size: 0.9rem;
}
.sme-refine__hunk {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin: 0.4rem 0;
}
.sme-refine__before,
.sme-refine__after {
  border-radius: var(--radius-sm);
  padding: 0.4rem 0.6rem;
  font-size: 0.82rem;
}
.sme-refine__before {
  background: rgba(219, 115, 99, 0.1);
  border: 1px solid rgba(219, 115, 99, 0.3);
}
.sme-refine__after {
  background: rgba(90, 160, 90, 0.12);
  border: 1px solid rgba(90, 160, 90, 0.35);
}
.sme-refine__before pre,
.sme-refine__after pre,
.sme-refine__fulltext {
  margin: 0.25rem 0 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
}
.sme-refine__tag {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
}
.sme-refine__full summary {
  cursor: pointer;
  color: var(--color-muted);
  font-size: 0.85rem;
}
.sme-refine__fulltext {
  max-height: 300px;
  overflow: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 0.5rem;
  font-size: 0.8rem;
}
.sme-refine__overrides {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.sme-refine__pill-reset {
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 700;
  padding: 0 0.15rem;
  color: inherit;
}
.sme-refine__history summary {
  cursor: pointer;
  color: var(--color-muted);
  font-size: 0.9rem;
}
.sme-refine__history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-top: 0.4rem;
}
.sme-refine__history-table td {
  padding: 0.3rem 0.5rem;
  border-top: 1px solid var(--color-border);
}
.sme-refine__note {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: rgba(39, 157, 182, 0.12);
  border: 1px solid rgba(39, 157, 182, 0.3);
  font-size: 0.8rem;
}
</style>
